/**
 * Типизированный клиент нашего REST API (STEP 11).
 * Авторизация — заголовком `Authorization: tma <initData>`, ровно как ждёт сервер.
 */
import { telegramEnv } from './telegram.js';

const BASE_URL = (import.meta.env['VITE_API_URL'] ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: unknown,
    message: string,
    fromServer = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.fromServer = fromServer;
  }

  /** 402 — не хватает токенов на цикл. */
  get isOutOfTokens(): boolean {
    return this.status === 402;
  }

  /** 409 — аккаунт брокера занят другим пользователем. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * true — текст ошибки прислал наш сервер и его можно показать как есть.
   * false — сообщение техническое (например, 502 от nginx), экран должен
   * подставить свою локализованную формулировку.
   */
  readonly fromServer: boolean = false;
}

/**
 * Текст ошибки, который не стыдно показать пользователю.
 * Возвращает null, когда сервер не прислал внятного сообщения (502 от nginx,
 * обрыв сети) — экран в этом случае подставляет свою локализованную фразу.
 */
export function serverError(err: unknown): string | null {
  if (err instanceof ApiError) return err.fromServer ? err.message : null;
  return null;
}

/**
 * Коды, которыми сервер отвечает вместо готовой фразы.
 *
 * Текст к ним лежит в словаре — иначе пользователь увидел бы служебное
 * `account_required`, да ещё и на языке сервера, а не на своём.
 */
const ERROR_KEYS: Record<string, string> = {
  account_required: 'app.error.account_required',
  broker_unavailable: 'app.error.broker_unavailable',
  // переключение на REAL: наш аккаунт + хотя бы один депозит
  deposit_required: 'app.error.real_deposit_required',
  foreign_account: 'app.error.real_foreign',
  // refresh-токен брокера умер и тихий перелогин не удался — нужен вход заново
  broker_session_expired: 'app.error.broker_session_expired',
};

/**
 * Готовый текст ошибки для экрана: сначала известный код, затем внятное
 * сообщение сервера, затем запасной ключ.
 */
export function apiMessage(
  err: unknown,
  t: (key: string) => string,
  fallbackKey: string,
): string {
  if (err instanceof ApiError) {
    const payload = err.payload;
    const code =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null;
    if (code && ERROR_KEYS[code]) return t(ERROR_KEYS[code]);
  }
  return serverError(err) ?? t(fallbackKey);
}

/**
 * Кого дёрнуть на 401. Подпись initData живёт сутки (INITDATA_MAX_AGE_SECONDS),
 * и вкладка, провисевшая дольше, получает 401 на всё — дальше запросы
 * бессмысленны, нужен экран «открой приложение заново» (AppProvider).
 */
let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { initData } = telegramEnv();

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(initData ? { authorization: `tma ${initData}` } : {}),
      ...init.headers,
    },
  });

  const text = await response.text();

  // не всякий ответ — наш JSON: 502/504 отдаёт nginx, и это HTML-страница.
  // Без этой защиты пользователь увидел бы сообщение парсера JSON
  let payload: unknown = null;
  try {
    payload = text.length > 0 ? (JSON.parse(text) as unknown) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.();
    const serverMessage =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null;
    // техническая строка на случай, когда сервер ничего внятного не сказал:
    // экран заменит её на свой текст по флагу fromServer
    throw new ApiError(
      response.status,
      payload,
      serverMessage ?? `HTTP ${response.status}`,
      serverMessage !== null,
    );
  }

  if (payload === null && text.length > 0) {
    throw new ApiError(response.status, null, `HTTP ${response.status}`, false);
  }

  return payload as T;
}

// ---------- типы ответов ----------

export interface Me {
  user: {
    telegramUserId: string;
    email: string | null;
    mode: 'DEMO' | 'REAL';
    language: string;
    tokens: number;
    stage: string | null;
  };
  balances: { demo: number; real: number; currency: string };
  broker: {
    linked: boolean;
    traderId: string | null;
    isVerified: boolean;
    /** false — аккаунт заведён вне нашей партнёрки. */
    isPartnerClient: boolean | null;
  };
  settings: {
    strategy: string;
    progression: number;
    maxSteps: number;
    maxTrades: number;
    expirationSeconds: number;
    autotrade: boolean;
  };
}

export interface TradeStep {
  index: number;
  amount: number;
  action: 'call' | 'put';
  won: boolean;
  profit: number;
  runningProfit: number;
  /** Пара шага — только в стратегии-«корзине», где каждый шаг на своей паре. */
  pair?: string;
  /** Подпись этой пары: `USDMXN_otc` → `USD/MXN OTC`. */
  pairLabel?: string;
}

/**
 * Состояние серии.
 *
 * Серия больше не считается «в одном ответе»: в режиме реальных демо-сделок шаг
 * длится настоящую экспирацию, поэтому сервер отдаёт `runId` сразу, а шаги
 * появляются по мере закрытия сделок у брокера.
 */
export interface TradeRun {
  runId: string;
  status: 'running' | 'done' | 'failed';
  pair: string;
  /** Подпись пары для экрана; `pair` остаётся символом брокера. */
  pairLabel?: string;
  action: 'call' | 'put';
  steps: TradeStep[];
  /** Сделка открыта и ждёт результата — её показываем отдельной строкой. */
  pending: { index: number; amount: number; action: 'call' | 'put' } | null;
  profit: number;
  won: boolean;
  tokensSpent: number;
  tokensLeft: number;
  balance: number;
  /** true — сделки реально ушли брокеру, false — симуляция. */
  live: boolean;
  /** real — сделки идут на реальном счёте (настоящие деньги). */
  market?: 'demo' | 'real';
  errorKey: string | null;
}

export interface Asset {
  symbol: string;
  label: string | null;
  type: string | null;
  payout: number | null;
  minTimeframe: number | null;
  maxTimeframe: number | null;
}

export interface Stats {
  total: number;
  won: number;
  lost: number;
  winRate: number;
  profit: number;
  tokensSpent: number;
  /** Самая длинная серия побед подряд. */
  bestStreak: number;
  /** Пара, по которой торговали чаще всего. */
  favouritePair: string | null;
  favouritePairLabel: string | null;
}

export interface TokenPackage {
  id: string;
  tokens: number;
  priceUsd: number;
  priceCryptoUsd: number;
  badge: 'star' | 'fire' | null;
}

export interface StrategyPreset {
  id: string;
  /** Витринное имя (PULSE, SNIPER…) — одинаково на всех языках. */
  label: string;
  emoji: string;
  progression: number;
  maxSteps: number;
  maxTrades: number;
  minBalanceUsd: number | null;
  manual?: boolean;
  /** Механика серии; отсутствует = классический Smart Scaling (recovery). */
  mode?: 'recovery' | 'flat' | 'paroli' | 'streak' | 'basket';
  /** streak: выигрышей до завершения серии. */
  targetWins?: number;
  /** basket: на скольких парах открываются сделки. */
  pairs?: number;
  /** Стратегия по таймеру («Автотрейдинг»): свой экран вместо серии. */
  timed?: boolean;
  /** Имя переводится — ключ i18n (у витринных PULSE/APEX его нет). */
  nameKey?: string;
}

export type AutotradingPace = 'fast' | 'normal' | 'calm';

export interface AutotradingParams {
  minPercent: number;
  maxPercent: number;
  durationMinutes: number;
  /** 0 — случайный таймфрейм на каждую сделку. */
  timeframeSeconds: number;
  pace: AutotradingPace;
  /** 0 — выключено. */
  stopLossPercent: number;
  /** 0 — выключено. */
  takeProfitPercent: number;
}

export interface AutotradingOptions {
  percent: number[];
  durationMinutes: number[];
  timeframeSeconds: number[];
  pace: AutotradingPace[];
  sessionLimitPercent: number[];
  paceMs: Record<AutotradingPace, [number, number]>;
  tokensPerTrade: number;
  enabled: boolean;
}

export interface AutotradingTrade {
  index: number;
  pair: string;
  pairLabel: string;
  action: 'call' | 'put';
  amount: number;
  timeframeSeconds: number;
  won: boolean;
  profit: number;
  closedAt: number;
}

export interface AutotradingSession {
  sessionId: string;
  status: 'running' | 'done' | 'stopped' | 'failed';
  params: AutotradingParams;
  tokensPerTrade: number;
  startedAt: number;
  endsAt: number;
  /** null — воркер ещё не взял сессию в работу. */
  claimedAt: number | null;
  finishedAt: number | null;
  trades: AutotradingTrade[];
  pending: {
    index: number;
    pair: string;
    pairLabel: string;
    action: 'call' | 'put';
    amount: number;
    timeframeSeconds: number;
    openedAt: number;
  } | null;
  nextTradeAt: number | null;
  profit: number;
  staked: number;
  wonTrades: number;
  tokensSpent: number;
  tokensLeft: number;
  startBalance: number;
  balance: number;
  live: boolean;
  market: 'demo' | 'real';
  stopReason: string | null;
  stopRequested: boolean;
}

export interface SettingsPayload {
  current: {
    strategy: string;
    progression: number;
    maxSteps: number;
    maxTrades: number;
    expirationSeconds: number;
    autotrade: boolean;
    language: string;
    manual: { progression: number; maxSteps: number; maxTrades: number };
    favouritePairs: string[];
    autotrading: AutotradingParams;
  };
  options: {
    strategies: StrategyPreset[];
    expirations: number[];
    manual: { progression: number[]; maxSteps: number[]; maxTrades: number[] };
    bets: Array<{ amount: number; risk?: boolean }>;
    autotrading?: AutotradingOptions;
  };
}

export interface ReferralsPayload {
  link: string;
  stats: {
    /** Все приглашённые, в любом статусе — именно это число показывает экран. */
    total: number;
    invited: number;
    trading: number;
    confirmed: number;
    pending: number;
    tokensEarned: number;
  };
  referrals: Array<{
    referredId: string;
    status: 'invited' | 'trading' | 'confirmed';
    tokensAwarded: number;
    createdAt: string;
  }>;
}

// ---------- методы ----------

export interface AppConfig {
  brand: {
    name: string;
    botUsername: string;
    botLink: string;
    supportContact: string;
    communityChannel: string;
  };
  /** Готовые ссылки на контакты (null — не задан): URL собирает сервер. */
  links?: {
    support: string | null;
    community: string | null;
    reviews: string | null;
  };
  locales: string[];
  defaultLocale: string;
  /** Пороги реф-начислений — с сервера, из того же конфига, что и реальные выплаты. */
  referralTiers?: Array<{ minDeposit: number; maxDeposit: number | null; tokens: number }>;
  /** Пул индикаторов «AI-скана»: каждый скан собирает из него случайный чек-лист. */
  scanIndicators?: ScanIndicatorGroup[];
  /** Токенов за $1 депозита — для текстов «{{rate}} за $1». */
  tokensPerDollar?: number;
}

export interface ScanIndicatorGroup {
  label: string;
  metrics: Array<{ name: string; min: number; max: number; digits: number; suffix?: string }>;
}

export const api = {
  /** Имя бренда и контакты приходят с сервера — на фронте их не хардкодим. */
  config: () => request<AppConfig>('/api/config'),

  /** Картинки, заменённые через админку (BACKLOG D7); версия языка сильнее дефолта. */
  media: (locale?: string) =>
    request<{ media: Record<string, string> }>(`/api/media${locale ? `?locale=${locale}` : ''}`),

  /** Словарь интерфейса под язык пользователя — общий с ботом. */
  texts: (locale?: string) =>
    request<{ locale: string; texts: Record<string, string> }>(
      `/api/texts${locale ? `?locale=${locale}` : ''}`,
    ),

  me: () => request<Me>('/api/me'),

  linkAccount: (email: string) =>
    request<{ status: 'created' | 'exists' | 'conflict'; password?: string; traderId?: string }>(
      '/api/account/link',
      { method: 'POST', body: JSON.stringify({ email }) },
    ),

  loginAccount: (email: string, password: string) =>
    request<{ status: 'linked' | '2fa' | 'invalid'; traderId?: string }>('/api/account/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  /** Выход из аккаунта брокера: связь удаляется, режим возвращается в DEMO. */
  unlinkAccount: () =>
    request<{ unlinked: boolean }>('/api/account/unlink', { method: 'POST' }),

  /** DEMO ↔ REAL. REAL — только наш аккаунт с депозитом, иначе 409 с кодом. */
  setMode: (mode: 'DEMO' | 'REAL') =>
    request<{ mode: string }>('/api/account/mode', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),

  runCycle: (input: { amount: number; pair?: string; action?: 'call' | 'put'; session?: boolean }) =>
    request<TradeRun>('/api/trade/cycle', { method: 'POST', body: JSON.stringify(input) }),

  /** Состояние серии — экран опрашивает его, пока `status === 'running'`. */
  tradeRun: (runId: string) => request<TradeRun>(`/api/trade/run/${runId}`),

  /** Незаконченная серия: если приложение закрыли на середине, вернёмся к ней. */
  activeRun: () => request<{ run: TradeRun | null }>('/api/trade/run'),

  assets: () => request<{ assets: Asset[] }>('/api/assets'),

  stats: () => request<Stats>('/api/stats'),

  packages: () => request<{ packages: TokenPackage[] }>('/api/tokens/packages'),

  depositLink: (packageId?: string) =>
    request<{ link: string }>('/api/tokens/deposit-link', {
      method: 'POST',
      body: JSON.stringify({ packageId }),
    }),

  withdrawalLink: () => request<{ link: string }>('/api/withdrawal-link', { method: 'POST' }),

  /** Кабинет брокера через наш редирект (клик логируется, домен — зеркало). */
  platformLink: () => request<{ link: string }>('/api/platform-link', { method: 'POST' }),

  settings: () => request<SettingsPayload>('/api/settings'),

  updateSettings: (patch: {
    strategy?: string;
    expirationSeconds?: number;
    autotrade?: boolean;
    language?: string;
    favouritePairs?: string[];
    manual?: Partial<Record<'progression' | 'maxSteps' | 'maxTrades', number>>;
    autotrading?: Partial<AutotradingParams>;
  }) => request<unknown>('/api/settings', { method: 'PATCH', body: JSON.stringify(patch) }),

  // «Автотрейдинг»: сессия по таймеру (services/autotrading.ts)
  autotradingStart: () =>
    request<AutotradingSession>('/api/autotrading/start', { method: 'POST' }),
  autotradingStop: () =>
    request<{ session: AutotradingSession | null }>('/api/autotrading/stop', { method: 'POST' }),
  autotradingActive: () =>
    request<{ session: AutotradingSession | null }>('/api/autotrading/session'),
  autotradingSession: (id: string) =>
    request<AutotradingSession>(`/api/autotrading/session/${id}`),

  referrals: () => request<ReferralsPayload>('/api/referrals'),
};
